import {
  HHButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import AddIcon from '@mui/icons-material/Add';
import { useCallback } from 'react';
import { EsiFeeScheduleForm } from '../components/esi-fee-schedules/esi-fee-schedule-form/form';
import { EsiFeeScheduleFormValues } from '../components/esi-fee-schedules/esi-fee-schedule-form/validation-schema';
import { EsiFeeScheduleGrid } from '../components/esi-fee-schedules/esi-fee-schedule-grid';
import { FeeScheduleViewToggle } from '../components/fee-schedules/fee-schedule-view-toggle';
import {
  DynamicDrawerProvider,
  useDynamicDrawerContext,
} from '../context/dynamic-drawer.context';
import { useSnackbarContext } from '../context/snackbar.context';
import { useBillingWriteAccess } from '../hooks/use-billing-write-access';
import {
  PaymentType,
  useCreateEsiFeeScheduleMutation,
  useGetPaymentTypesQuery,
} from '../types';

export const ESI_FEE_SCHEDULES_TEST_ID = 'esi-fee-schedules-container';

const EMPTY_PAYMENT_TYPES: PaymentType[] = [];

const EsiFeeSchedulesContent = (): JSX.Element => {
  const canWrite = useBillingWriteAccess();
  const { setDrawerContent, onClose } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();

  const { data: paymentTypesData, loading: paymentTypesLoading } =
    useGetPaymentTypesQuery();
  const paymentTypes = paymentTypesData?.getPaymentTypes ?? EMPTY_PAYMENT_TYPES;

  const [createEsiFeeSchedule] = useCreateEsiFeeScheduleMutation();

  const handleCreate = useCallback(
    async (values: EsiFeeScheduleFormValues): Promise<void> => {
      try {
        await createEsiFeeSchedule({
          variables: {
            esiFeeScheduleInput: {
              paymentType: values.paymentType,
              price: values.price as number,
              upc: values.upc,
              program: values.program || null,
              description: values.description || null,
            },
          },
          refetchQueries: ['getEsiFeeSchedules'],
        });
        setSnackbarState(
          'ESI fee schedule created successfully',
          'success',
          3000,
        );
        onClose();
      } catch {
        setSnackbarState('Error creating ESI fee schedule', 'error', 3000);
      }
    },
    [createEsiFeeSchedule, setSnackbarState, onClose],
  );

  const handleAddRecord = (): void => {
    setDrawerContent(
      <HHStack padding={4}>
        <EsiFeeScheduleForm
          onSubmit={handleCreate}
          paymentTypes={paymentTypes}
          paymentTypesLoading={paymentTypesLoading}
        />
      </HHStack>,
      <HHTypography hhVariant="h6">Add ESI UPC</HHTypography>,
      undefined,
      'temporary',
    );
  };

  return (
    <HHStack
      flex={1}
      direction="column"
      spacing={4}
      minHeight="0"
      data-testid={ESI_FEE_SCHEDULES_TEST_ID}
    >
      <HHStack
        sx={{ paddingLeft: 6, paddingRight: 6, paddingTop: 6 }}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <HHStack direction="row" alignItems="center" spacing={5}>
          <HHStack sx={{ minWidth: 320 }}>
            <HHTypography hhVariant="section-title">
              ESI UPC Schedules
            </HHTypography>
          </HHStack>
          <FeeScheduleViewToggle currentView="esi" />
        </HHStack>
        {canWrite && (
          <HHButton
            hhVariant="variant-bypass"
            variant="contained"
            size="medium"
            startIcon={<AddIcon />}
            onClick={handleAddRecord}
            data-testid="add-esi-fee-schedule-button"
            sx={{ minWidth: 120 }}
          >
            Add UPC
          </HHButton>
        )}
      </HHStack>
      <EsiFeeScheduleGrid
        paymentTypes={paymentTypes}
        paymentTypesLoading={paymentTypesLoading}
      />
    </HHStack>
  );
};

const EsiFeeSchedules = (): JSX.Element => (
  <DynamicDrawerProvider>
    <EsiFeeSchedulesContent />
  </DynamicDrawerProvider>
);

export default EsiFeeSchedules;
